import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, EffectCoverflow } from "swiper/modules";
import "swiper/css";
import "swiper/css/effect-coverflow";
import { useRef, useState } from "react";
import { motion } from "framer-motion";
import AnimatedSection from "./AnimatedSection";
import SceneOrb from "./SceneOrb";

import img1 from "../assets/Start Your Live Party.png";
import img2 from "../assets/Rooms.png";
import img3 from "../assets/Send Gifts.png";
import img4 from "../assets/Room Levels.png";
import img5 from "../assets/Family Space.png";
import img6 from "../assets/Messages.png";
import img7 from "../assets/Explore.png";
import img8 from "../assets/Profile.png";
import img9 from "../assets/Payment Methods.png";
import img10 from "../assets/Withdraw.png";

const sliderData = [
  { img: img1, text: "Enjoy the live streaming fun" },
  { img: img2, text: "Join rooms worldwide" },
  { img: img3, text: "Show love with live gifts" },
  { img: img4, text: "Level up and earn more" },
  { img: img5, text: "Stay connected anytime" },
  { img: img6, text: "View top streamers and events" },
  { img: img7, text: "Discover broadcasters across the globe" },
  { img: img8, text: "Explore profiles and activity" },
  { img: img9, text: "Manage local payment options" },
  { img: img10, text: "Withdraw with confidence" },
];

export default function Front() {
  const swiperRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <section className="relative overflow-hidden px-4 pb-24 pt-28 sm:px-6 lg:px-8">
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_top_left,_rgba(255,122,61,0.2),transparent_40%)]" />
      <div className="mx-auto flex max-w-7xl flex-col gap-12 lg:flex-row lg:items-center lg:gap-16">
        <AnimatedSection className="max-w-2xl flex-1 space-y-8" delay={0.1}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center rounded-full border border-orange-400/30 bg-orange-500/10 px-4 py-2 text-sm font-medium text-orange-200 backdrop-blur"
          >
            Premium live experiences, reimagined
          </motion.div>
          <div className="space-y-5">
            <h1 className="text-4xl font-semibold tracking-tight text-white sm:text-5xl lg:text-7xl">
              Build your next <span className="bg-gradient-to-r from-orange-300 via-orange-500 to-fuchsia-500 bg-clip-text text-transparent">live community</span> in motion.
            </h1>
            <p className="max-w-xl text-lg leading-8 text-slate-300">
              Hunt Share Live brings creators, viewers, agencies, and digital rewards together in one elegant, interactive environment designed for connection and growth.
            </p>
          </div>
          <div className="flex flex-wrap gap-4">
            <a href="/contact" className="rounded-full bg-gradient-to-r from-orange-500 to-fuchsia-600 px-6 py-3 font-semibold text-white shadow-[0_20px_45px_rgba(255,122,61,0.25)] transition hover:scale-[1.02]">
              Contact the team
            </a>
            <a href="/services" className="rounded-full border border-white/15 bg-white/10 px-6 py-3 font-semibold text-slate-100 backdrop-blur transition hover:border-orange-300/50 hover:bg-white/20">
              Explore services
            </a>
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            {[
              ["10K+", "active rooms"],
              ["24/7", "creator support"],
              ["Instant", "wallet access"],
            ].map(([value, label]) => (
              <div key={label} className="rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur">
                <p className="text-xl font-semibold text-white">{value}</p>
                <p className="text-sm text-slate-400">{label}</p>
              </div>
            ))}
          </div>
        </AnimatedSection>

        <AnimatedSection className="flex-1" delay={0.2}>
          <SceneOrb />
        </AnimatedSection>
      </div>

      <AnimatedSection className="mx-auto mt-16 max-w-7xl" delay={0.25}>
        <div className="rounded-[2rem] border border-white/10 bg-slate-900/60 p-5 shadow-[0_30px_80px_rgba(0,0,0,0.35)] backdrop-blur-xl">
          <Swiper
            modules={[Autoplay, EffectCoverflow]}
            effect="coverflow"
            centeredSlides
            loop
            spaceBetween={18}
            slidesPerView={2.5}
            onSwiper={(swiper) => (swiperRef.current = swiper)}
            onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
            autoplay={{ delay: 2400, disableOnInteraction: false }}
            coverflowEffect={{ rotate: 18, stretch: 0, depth: 120, modifier: 2.5 }}
            breakpoints={{
              320: { slidesPerView: 1.8 },
              640: { slidesPerView: 2.5 },
              1024: { slidesPerView: 3.4 },
            }}
          >
            {sliderData.map((slide, index) => {
              const isActive = activeIndex === index;
              return (
                <SwiperSlide key={index}>
                  <button
                    type="button"
                    onClick={() => swiperRef.current?.slideToLoop(index)}
                    className={`group w-full overflow-hidden rounded-[1.5rem] border text-left transition-all ${isActive ? "border-orange-400/50 shadow-[0_20px_60px_rgba(255,122,61,0.22)]" : "border-white/10"}`}
                  >
                    <img src={slide.img} alt={slide.text} className="h-56 w-full object-contain bg-gradient-to-br from-slate-950 to-slate-800 p-4" />
                    <div className="bg-slate-950/90 p-4">
                      <p className="text-sm font-medium text-slate-300 transition group-hover:text-white">{slide.text}</p>
                    </div>
                  </button>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>
      </AnimatedSection>
    </section>
  );
}
